import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';

export type ExportField = {
  key: string;
  label: string;
  description?: string;
  defaultSelected: boolean;
};

export const EXPORT_FIELDS: ExportField[] = [
  { key: 'id', label: 'ID', defaultSelected: false },
  { key: 'title', label: 'Title', description: 'The main title of the content', defaultSelected: true },
  { key: 'content', label: 'Content', description: 'Full content text', defaultSelected: true },
  { key: 'content_type', label: 'Content Type', description: 'tip, fact, quote, etc.', defaultSelected: true },
  { key: 'category', label: 'Category', defaultSelected: true },
  { key: 'status', label: 'Status', description: 'published, draft, etc.', defaultSelected: true },
  { key: 'is_featured', label: 'Featured', defaultSelected: false },
  { key: 'view_count', label: 'View Count', defaultSelected: true },
  { key: 'source', label: 'Source', description: 'Content source or blog post', defaultSelected: false },
  { key: 'slug', label: 'Slug', defaultSelected: false },
  { key: 'image_url', label: 'Image URL', defaultSelected: false },
  { key: 'created_at', label: 'Created Date', defaultSelected: true },
  { key: 'updated_at', label: 'Updated Date', defaultSelected: false },
  { key: 'publish_date', label: 'Publish Date', defaultSelected: false },
  { key: 'author_id', label: 'Author ID', defaultSelected: false },
  { key: 'display_order', label: 'Display Order', defaultSelected: false },
];

interface ExportFieldSelectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  exportType: 'pdf' | 'csv' | 'json';
  selectedFields: Set<string>;
  onFieldToggle: (fieldKey: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onConfirm: () => void;
  itemCount: number;
}

export function ExportFieldSelector({
  open,
  onOpenChange,
  exportType,
  selectedFields,
  onFieldToggle,
  onSelectAll,
  onDeselectAll,
  onConfirm,
  itemCount,
}: ExportFieldSelectorProps) {
  const exportTypeLabels = {
    pdf: 'PDF',
    csv: 'CSV',
    json: 'JSON',
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Select Fields to Export</DialogTitle>
          <DialogDescription>
            Choose which fields to include in your {exportTypeLabels[exportType]} export.
            Exporting {itemCount} {itemCount === 1 ? 'item' : 'items'}.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 mb-4">
          <Button variant="outline" size="sm" onClick={onSelectAll}>
            Select All
          </Button>
          <Button variant="outline" size="sm" onClick={onDeselectAll}>
            Deselect All
          </Button>
        </div>

        <ScrollArea className="h-[400px] pr-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {EXPORT_FIELDS.map((field) => (
              <div key={field.key} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-accent/50 transition-colors">
                <Checkbox
                  id={`field-${field.key}`}
                  checked={selectedFields.has(field.key)}
                  onCheckedChange={() => onFieldToggle(field.key)}
                />
                <div className="flex-1 space-y-1">
                  <Label
                    htmlFor={`field-${field.key}`}
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    {field.label}
                  </Label>
                  {field.description && (
                    <p className="text-xs text-muted-foreground">
                      {field.description}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={selectedFields.size === 0}>
            Export {selectedFields.size} {selectedFields.size === 1 ? 'Field' : 'Fields'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
